import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { createComponent } from '@ycos/fela'

const Container = createComponent(
  'GridContainer',
  ({ theme, additionalStyles, small, medium, large, xlarge, divider, fullBleedConf }) => {
    const columns = {
      small: {
        width: (100 / theme.breakpoints.small.columnCount) * small.width,
        offset: (100 / theme.breakpoints.small.columnCount) * small.offset
      },
      medium: {
        width: (100 / theme.breakpoints.medium.columnCount) * medium.width,
        offset: (100 / theme.breakpoints.medium.columnCount) * medium.offset
      },
      large: {
        width: (100 / theme.breakpoints.large.columnCount) * large.width,
        offset: (100 / theme.breakpoints.large.columnCount) * large.offset
      },
      xlarge: {
        width: (100 / theme.breakpoints.xlarge.columnCount) * xlarge.width,
        offset: (100 / theme.breakpoints.xlarge.columnCount) * xlarge.offset
      }
    }

    // 11px = 1px + 10px = line thickness + offset of the line from the gutter
    const offsetMap = {
      medium: theme.breakpoints.medium.gutterWidth + 11,
      large: theme.breakpoints.large.gutterWidth + 11,
      xlarge: theme.breakpoints.xlarge.gutterWidth + 11
    }

    const dividerStyles = {
      'screen-medium': {
        ':before': {
          content: '" "',
          position: 'absolute',
          top: '16px',
          right: divider === 'right' ? 0 : undefined,
          bottom: 0,
          width: '1px',
          background: `${theme.custom.divider.color}`,
          marginRight: divider === 'right' ? `-${offsetMap.medium}px` : undefined,
          marginLeft: divider === 'left' ? `-${offsetMap.medium}px` : undefined
        }
      },
      'screen-large': {
        ':before': {
          marginRight: divider === 'right' ? `-${offsetMap.large}px` : undefined,
          marginLeft: divider === 'left' ? `-${offsetMap.large}px` : undefined
        }
      },
      'screen-xlarge': {
        ':before': {
          marginRight: divider === 'right' ? `-${offsetMap.xlarge}px` : undefined,
          marginLeft: divider === 'left' ? `-${offsetMap.xlarge}px` : undefined
        }
      }
    }

    return {
      position: 'relative',
      boxSizing: 'content-box',
      flex: '1 0 auto',
      float: 'left',
      flexBasis: fullBleedConf.fullBleed || fullBleedConf.small ? `calc(${columns.small.width}%)` : `calc(${columns.small.width}% - ${theme.breakpoints.small.gutterWidth}px)`,
      maxWidth: fullBleedConf.fullBleed || fullBleedConf.small ? `calc(${columns.small.width}%)` : `calc(${columns.small.width}% - ${theme.breakpoints.small.gutterWidth}px)`,
      marginLeft: fullBleedConf.fullBleed || fullBleedConf.small ? '0px' : `${theme.breakpoints.small.gutterWidth}px`,
      paddingLeft: fullBleedConf.fullBleed || fullBleedConf.small ? '0px' : `${columns.small.offset}%`,
      ...additionalStyles,
      ...getAllBreakpointsStyle(theme, divider, dividerStyles, columns, additionalStyles, fullBleedConf)
    }
  },
  'div'
)

const getAllBreakpointsStyle = (theme, divider, dividerStyles, columns, additionalStyles, fullBleedConf) =>
  Object.values(theme.breakpoints).reduce(
    (accumulator, breakpoint) => ({
      ...accumulator,
      ...getBreakpointStyle(theme, breakpoint, divider, dividerStyles, columns, additionalStyles, fullBleedConf)
    }),
    {}
  )

const getBreakpointStyle = (theme, breakpoint, divider, dividerStyles, columns, additionalStyles, fullBleedConf) => {
  return (
    breakpoint.name.toLowerCase() !== 'small' && {
      [`screen-${breakpoint.name.toLowerCase()}`]: {
        flexBasis: fullBleedConf.fullBleed || fullBleedConf?.[breakpoint.name.toLowerCase()] ? `calc(${columns?.[breakpoint.name.toLowerCase()].width}%)` : `calc(${columns?.[breakpoint.name.toLowerCase()].width}% - ${theme.breakpoints[breakpoint.name.toLowerCase()].gutterWidth}px)`,
        maxWidth: fullBleedConf.fullBleed || fullBleedConf?.[breakpoint.name.toLowerCase()] ? `calc(${columns?.[breakpoint.name.toLowerCase()].width}%)` : `calc(${columns?.[breakpoint.name.toLowerCase()].width}% - ${theme.breakpoints[breakpoint.name.toLowerCase()].gutterWidth}px)`,
        marginLeft: fullBleedConf.fullBleed || fullBleedConf?.[breakpoint.name.toLowerCase()] ? '0px' : `${theme.breakpoints?.[breakpoint.name.toLowerCase()].gutterWidth}px`,
        paddingLeft: fullBleedConf.fullBleed || fullBleedConf?.[breakpoint.name.toLowerCase()] ? '0px' : `${columns?.[breakpoint.name.toLowerCase()].offset}%`,
        ...additionalStyles[`screen-${breakpoint.name.toLowerCase()}`],
        ...(divider !== null ? dividerStyles[`screen-${breakpoint.name.toLowerCase()}`] : [])
      }
    }
  )
}

export class GridContext extends Component {
  // to ensure that, if we don't get the object with the expected properties, we set it to undefined to be able to default it more easily
  static getSizeOrEmpty(size) {
    return size && !isNaN(size.width) && !isNaN(size.offset) ? size : undefined
  }

  getChildContext() {
    const sizes = this.getSizes()
    return {
      gridContext: {
        small: sizes.small.width,
        medium: sizes.medium.width,
        large: sizes.large.width,
        xlarge: sizes.xlarge.width
      }
    }
  }

  getSizes = () => {
    const { small, medium, large, xlarge, fullBleedConf = {} } = this.props;

    const smallS = GridContext.getSizeOrEmpty(small)
    const mediumS = GridContext.getSizeOrEmpty(medium)
    const largeS = GridContext.getSizeOrEmpty(large)
    const xlargeS = GridContext.getSizeOrEmpty(xlarge)

    const defaultSmall = { width: 6, offset: 0 }
    const defaultMedium = { width: 12, offset: 0 }

    return {
      small: fullBleedConf.small ? fullBleedConf.sizes.small : smallS || defaultSmall,
      medium: fullBleedConf.medium ? fullBleedConf.sizes.medium : mediumS || defaultMedium,
      large: fullBleedConf.large ? fullBleedConf.sizes.large || fullBleedConf.sizes.medium : largeS || mediumS || defaultMedium,
      xlarge: fullBleedConf.xlarge ? fullBleedConf.sizes.xlarge || fullBleedConf.sizes.large || fullBleedConf.sizes.medium : xlargeS || largeS || mediumS || defaultMedium
    }
  }

  render() {
    return this.props.children
  }
}

GridContext.childContextTypes = {
  gridContext: PropTypes.shape({
    small: PropTypes.number.isRequired,
    medium: PropTypes.number.isRequired,
    large: PropTypes.number,
    xlarge: PropTypes.number
  })
}

GridContext.contextTypes = {
  gridContext: PropTypes.shape({
    small: PropTypes.number.isRequired,
    medium: PropTypes.number.isRequired,
    large: PropTypes.number,
    xlarge: PropTypes.number
  })
}

class Grid extends GridContext {
  render() {
    const { children, additionalStyles, divider, fullBleedConf = {}, fullBleed } = this.props
    const sizes = this.getSizes()

    return (
      <Container small={sizes.small} medium={sizes.medium} large={sizes.large} xlarge={sizes.xlarge} additionalStyles={additionalStyles} divider={divider} fullBleedConf={{ ...fullBleedConf, fullBleed }}>
        {children}
      </Container>
    )
  }
}

Grid.defaultProps = {
  small: {
    width: 6,
    offset: 0
  },
  medium: {
    width: 12,
    offset: 0
  },
  additionalStyles: {},
  divider: null
}

Grid.propTypes = {
  children: PropTypes.node,
  small: PropTypes.shape({
    width: PropTypes.number,
    offset: PropTypes.number
  }),
  medium: PropTypes.shape({
    width: PropTypes.number,
    offset: PropTypes.number
  }),
  large: PropTypes.shape({
    width: PropTypes.number,
    offset: PropTypes.number
  }),
  xlarge: PropTypes.shape({
    width: PropTypes.number,
    offset: PropTypes.number
  }),
  divider: PropTypes.string,
  additionalStyles: PropTypes.object
}

export default Grid
